import { InteractionEvent } from 'pixi.js';
import ConsoleUtil from "../utils/ConsoleUtil";

/**
 * A singleton class, used to execute various commands.
 * The command must be set, before its usage. This is typically done at the game specific index.ts.
 * Usage example:
 *
 * ```ts
 * // Usually we define the command at the game index.ts
 * CommandManager.setCommand(Commands.SPIN, GameCommands.SpinCommand);
 *
 * // Use from anywhere.
 * CommandManager.execute(Commands.SPIN);
 * ```
 *
 *
 * @class CommandManager
 */
class CommandManager {
    static  commandsSet: Map<string , (...args: any[]) => void>;

    /**
     * Creates an instance of CommandManager.
     * @memberof CommandManager
     */
    constructor() {
        CommandManager.commandsSet = new Map<string , (...args: any[]) => void>();
    }

    /**
     * Checks whether a command with given name is already set/defined.
     *
     * @param {string} commandName The command name to check.
     * @return {*}  {boolean} True if the command already exists, otherwise false.
     * @memberof CommandManager
     */
    public has(commandName: string): boolean {
        return CommandManager.commandsSet.has(commandName);
    }

    /**
     * Adds new or changes existing command with the name specified.
     *
     * @param {string} commandName The name of the command to add/modify.
     * @param {(...args: any[]) => void} command The function to execute.
     * @memberof CommandManager
     */
    public setCommand(commandName: string, command: (...args: any[]) => void) {
        CommandManager.commandsSet.set(commandName, command);
    }

    /**
     * Executes a command with the name specified, optionally with the provided arguments.
     * In case the command with such name does not exists, it will warn the console with red color.
     *
     * @param {string} commandName The name of the command to execute.
     * @param {*} [args] The optional arguments to pass to the command function.
     * @param {*} [event] The optional arguments to pass to the command function.
     * @memberof CommandManager
     */
    public execute( commandName: string, args?: any, event?: InteractionEvent) {
        if (CommandManager.commandsSet.has(commandName)) {
            CommandManager.commandsSet.get(commandName)(args, event);
        } else {
            ConsoleUtil.red("Command " + commandName + " not found!");
        }
    }

}

export default new CommandManager();
